import { Link } from 'react-router-dom';
import { ExternalLink, Lock, Package, Unlock } from 'lucide-react';

import { cn } from '@/lib/cn';
import { fmtInt } from '@/lib/format';
import { useWineBoxes, type Box, type BoxSlot } from './useBoxes';

/**
 * Panel que se muestra en el detalle del vino: lista las cajas físicas donde
 * aparece este vino. Por cada caja:
 *   - mini grilla con todas las posiciones, resaltando solo donde está este vino
 *   - cuenta cuántas posiciones ocupa
 *   - link a editar la caja
 */
export function WineBoxesPanel({ wineId }: { wineId: number }) {
  const { data: boxes, isLoading } = useWineBoxes(wineId);

  if (isLoading) {
    return (
      <div className="card p-4 md:p-5 text-notion-500 text-sm lg:col-span-3">
        Cargando cajas…
      </div>
    );
  }
  if (!boxes || boxes.length === 0) {
    return (
      <div className="card p-4 md:p-5 lg:col-span-3 text-sm text-notion-500">
        <h2 className="font-medium flex items-center gap-2 mb-1 text-notion-800">
          <Package size={16} className="text-notion-500" /> En cajas
        </h2>
        Este vino todavía no está asignado a ninguna caja física.{' '}
        <Link to="/boxes" className="text-emerald-700 hover:underline">
          Ir a Cajas
        </Link>{' '}
        para asignarlo.
      </div>
    );
  }

  // Total de posiciones de este vino sumadas en todas las cajas.
  const totalPositions = boxes.reduce(
    (acc, b) => acc + (b.slots?.filter((s) => s.wine_id === wineId).length ?? 0),
    0,
  );

  return (
    <div className="card p-4 md:p-5 space-y-3 lg:col-span-3">
      <h2 className="font-medium flex items-center justify-between">
        <span className="flex items-center gap-2">
          <Package size={16} className="text-notion-500" />
          En cajas
        </span>
        <span className="text-xs text-notion-500 font-normal">
          {fmtInt(totalPositions)} {totalPositions === 1 ? 'posición' : 'posiciones'} en{' '}
          {fmtInt(boxes.length)} {boxes.length === 1 ? 'caja' : 'cajas'}
        </span>
      </h2>

      <ul className="space-y-3">
        {boxes.map((box) => (
          <BoxRow key={box.id} box={box} wineId={wineId} />
        ))}
      </ul>
    </div>
  );
}

function BoxRow({ box, wineId }: { box: Box; wineId: number }) {
  const slots = box.slots ?? [];
  const mineSlots = slots.filter((s) => s.wine_id === wineId);
  const slotMap = new Map<string, BoxSlot>();
  slots.forEach((s) => slotMap.set(`${s.row}-${s.col}`, s));

  // Filas de arriba hacia abajo (row=rows arriba, row=1 abajo).
  const rowsTopDown = Array.from({ length: box.rows }, (_, i) => box.rows - i);
  const colsLeftRight = Array.from({ length: box.cols }, (_, i) => i + 1);

  return (
    <li className="border-l-2 border-emerald-300 pl-3 py-1">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="font-medium text-sm flex items-center gap-2">
            Caja {box.box_number}
            {box.status === 'confirmed' ? (
              <Lock size={11} className="text-emerald-600" aria-label="Cerrada" />
            ) : (
              <Unlock size={11} className="text-amber-600" aria-label="En planificación" />
            )}
          </p>
          <p className="text-xs text-notion-500">
            {box.rows}×{box.cols} · {mineSlots.length}{' '}
            {mineSlots.length === 1 ? 'posición de este vino' : 'posiciones de este vino'}
            {' · '}
            {mineSlots.map((s) => `F${s.row}·C${s.col}`).join(', ')}
          </p>
        </div>
        <Link
          to={`/boxes/${box.id}`}
          className="text-xs text-notion-500 hover:text-notion-800 inline-flex items-center gap-1 shrink-0"
        >
          Ver caja <ExternalLink size={11} />
        </Link>
      </div>

      {/* Mini grilla: resalta las posiciones de este vino. */}
      <div
        className="inline-grid gap-0.5 border border-notion-200 p-1 rounded bg-notion-50"
        style={{ gridTemplateColumns: `repeat(${box.cols}, minmax(0, 1fr))` }}
      >
        {rowsTopDown.flatMap((row) =>
          colsLeftRight.map((col) => {
            const s = slotMap.get(`${row}-${col}`);
            const isMine = s?.wine_id === wineId;
            const isOther = s?.wine_id != null && s.wine_id !== wineId;
            return (
              <div
                key={`${row}-${col}`}
                className={cn(
                  'w-6 h-6 rounded-sm border',
                  isMine && s?.status === 'confirmed' && 'bg-emerald-600 border-emerald-700',
                  isMine && s?.status === 'planned' && 'bg-amber-300 border-amber-400',
                  isOther && 'bg-notion-200 border-notion-300',
                  !s?.wine_id && 'bg-white border-notion-200',
                )}
                title={
                  s?.wine
                    ? `F${row}·C${col}: ${s.wine.name} ${s.wine.vintage ?? ''}`
                    : `F${row}·C${col}: vacía`
                }
              />
            );
          }),
        )}
      </div>
    </li>
  );
}
